import type {ComponentProps, MouseEvent} from "react";
import {usePagination, type UsePaginationProps} from 'react-instantsearch';
import {useConfigContext} from "@/ConfigProvider";

export function Pagination(props: UsePaginationProps) {
    const {
        pages,
        currentRefinement,
        nbPages,
        isFirstPage,
        isLastPage,
        refine,
        createURL,
    } = usePagination(props);
    const firstPageIndex = 0;
    const {padding = 5} = props;
    const previousPageIndex = currentRefinement - 1;
    const nextPageIndex = currentRefinement + 1;
    const lastPageIndex = nbPages - 1;
    const {tc, algoliaConfig: {categoryToDisplay}} = useConfigContext();
    if (!nbPages || nbPages <= 1) return <></>;
    return (
        <div className={"pagination"}>
            {
                (currentRefinement != firstPageIndex) &&
                <PaginationItem
                    isDisabled={isFirstPage}
                    href={createURL(previousPageIndex)}
                    onClick={() => refine(previousPageIndex)}
                    className={"pagination__item pagination__item--prev"}
                    aria-label={tc.previousPage}
                >
                    <i className="icon icon-arrow-left" aria-hidden={true}></i>
                </PaginationItem>
            }
            {
                ((currentRefinement - padding * 2) > firstPageIndex && (nbPages > (1 + 2 * padding))) &&
                <>
                    <PaginationItem
                        isDisabled={isFirstPage}
                        href={createURL(firstPageIndex)}
                        onClick={() => refine(firstPageIndex)}
                        className={"pagination__item"}
                        aria-label={categoryToDisplay ? `${categoryToDisplay} - ${tc.page} 1` : `${tc.page} 1`}
                    >
                        1
                    </PaginationItem>
                    <span className="pagination__item pagination__item--seperator" aria-hidden={true}>...</span>
                </>
            }

            {pages.map((page) => {
                const label = page + 1;

                return (
                    <PaginationItem
                        key={page}
                        isDisabled={false}
                        aria-label={categoryToDisplay ? `${categoryToDisplay} - ${tc.page} ${label}` : `${tc.page} ${label}`}
                        href={createURL(page)}
                        onClick={() => refine(page)}
                        className={"pagination__item " + (currentRefinement === page ? "pagination__item--active" : "")}
                    >
                        {label}
                    </PaginationItem>
                );
            })}

            {
                ((currentRefinement + padding * 2) < lastPageIndex && (nbPages > (1 + 2 * padding))) &&
                <>
                    <span className="pagination__item pagination__item--seperator">...</span>
                    <PaginationItem
                        isDisabled={isLastPage}
                        href={createURL(lastPageIndex)}
                        onClick={() => refine(lastPageIndex)}
                        className={"pagination__item"}
                        aria-label={categoryToDisplay ? `${categoryToDisplay} - ${tc.page} ${nbPages}` : `${tc.page} ${nbPages}`}>
                        {nbPages}
                    </PaginationItem>
                </>
            }
            {
                (currentRefinement != lastPageIndex) &&
                <PaginationItem
                    isDisabled={isLastPage}
                    href={createURL(nextPageIndex)}
                    onClick={() => refine(nextPageIndex)}
                    className={"pagination__item pagination__item--next"}
                    aria-label={tc.nextPage}
                >
                    <i className="icon icon-arrow-right" aria-hidden={true}></i>
                </PaginationItem>
            }

        </div>
    );
}
type PaginationItemProps = Omit<ComponentProps<'a'>, 'onClick'> & {
    onClick: NonNullable<ComponentProps<'a'>['onClick']>;
    isDisabled: boolean;
};
function PaginationItem({
                            isDisabled,
                            href,
                            onClick,
                            ...props
                        }: PaginationItemProps) {
    if (isDisabled) {
        return (
            <span {...props} />
        );
    }

    return (
        <a
            href={href}
            onClick={(event:MouseEvent<HTMLAnchorElement>) => {
                if (isModifierClick(event)) {
                    return;
                }

                event.preventDefault();

                onClick(event);
            }}
            {...props}
        />
    );
}
function isModifierClick(event:  MouseEvent) {
    const isMiddleClick = event.button === 1;
    return Boolean(
        isMiddleClick ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey
    );
}